// services
import type { HTTPParams } from "@shared/types/HTTP";
import { HttpPromiseWithEntireResponse, Http } from "@shared/svelte/Modules/ApiService";

// stores
import { creditsStore } from "@retailer/stores/CreditsStore";

//types
import type { CreditResource } from "@retailer/stores/CreditsStore";
import type { CreditToApply } from "@shared/types/Credit";
export type CreditStatus = "available" | "applied" | "scheduled" | "pending" | "closed";

export interface CreditIndexParams {
  distributor_ids: string;
  search: string;
  page: string;
  start_date?: string;
  end_date?: string;
  status?: CreditStatus;
}

const baseUrl = "/api/retailer/credits/";

const extractPagination = (response, pageNum) => {
  let perPage, totalEntries;
  for (let pair of response.headers.entries()) {
    const key = pair[0];
    switch (key) {
      case "x-pagination-limit":
        perPage = parseInt(pair[1]);
        break;
      case "x-pagination-entries":
        totalEntries = parseInt(pair[1]);
        break;
    }
  }

  return {
    page: Number(pageNum || 1),
    perPage: perPage,
    totalEntries: totalEntries,
  };
};

export const setLoadedState = (resource: CreditResource, loadedStatus: boolean) => {
  creditsStore.update(state => ({
    ...state,
    loaded: {
      ...state.loaded,
      [resource]: loadedStatus,
    },
    failed: false,
  }));
};

// get invoices index
export const index = async (filters: CreditIndexParams) => {
  setLoadedState("index", false);

  if (!filters.page) {
    filters.page = "1";
  }

  // remove the optional filters if they are undefined
  ["start_date", "end_date", "status", "search", "distributor_ids"].forEach((filterKey: string) => {
    if (!filters[filterKey] || filters[filterKey] == "undefined") delete filters[filterKey];
  });

  // request payload
  const urlParams = new URLSearchParams({ ...filters });

  const requestData = {
    method: "GET",
    url: baseUrl + "?" + urlParams,
  };

  // request
  const response = await HttpPromiseWithEntireResponse(requestData);

  let json = [];

  // callbacks
  if (response.ok) {
    json = await response.json();
  }

  creditsStore.update(state => ({
    ...state,
    credits: json,
    failed: !response.ok,
    pagination: extractPagination(response, filters.page),
    loaded: {
      ...state.loaded,
      index: true,
    },
  }));
};

export const getCreditsWithAmounts = async (
  distributorIds: number[],
): Promise<{ [k: string]: CreditToApply[] }> => {
  const urlParams = new URLSearchParams();
  distributorIds.forEach(id => urlParams.append("distributor_ids[]", id.toString()));

  const requestData = {
    method: "GET",
    url: baseUrl + "available_with_amounts?" + urlParams,
  };

  const successCB = credits => {
    const creditMap = {};

    credits.forEach(credit => {
      if (!creditMap[credit.distributor_name]) creditMap[credit.distributor_name] = [];
      creditMap[credit.distributor_name].push(credit);
    });

    return creditMap;
  };

  const creditsForDistributors = await Http(requestData, successCB, json =>
    console.log("ERROR: ", json),
  );

  return creditsForDistributors;
};

// get invoices index
export const getRedemptions = async (invoiceId: number) => {
  setLoadedState("redemptions", false);

  // request payload
  const urlParams = new URLSearchParams({
    invoice_id: invoiceId.toString(),
  });

  const requestData = {
    method: "GET",
    url: baseUrl + "redemptions?" + urlParams,
  };

  const successCB = json =>
    creditsStore.update(state => ({
      ...state,
      redemptions: json,
      loaded: {
        ...state.loaded,
        redemptions: true,
      },
      failed: false,
    }));

  const errCB = () =>
    creditsStore.update(state => ({
      ...state,
      loaded: {
        ...state.loaded,
        redemptions: true,
      },
      failed: true,
    }));

  await Http(requestData, successCB, errCB);
};

export const getAvailable = async () => {
  setLoadedState("available", false);
  const requestData: HTTPParams = {
    method: "GET",
    url: baseUrl + "available",
  };

  // request
  const response = await HttpPromiseWithEntireResponse(requestData);

  if (response.ok) {
    const res = await response.json();
    creditsStore.update(state => ({
      ...state,
      available: res,
      loaded: {
        ...state.loaded,
        available: true,
      },
      failed: false,
    }));
  } else {
    creditsStore.update(state => ({
      ...state,
      loaded: {
        ...state.loaded,
        available: true,
      },
      failed: true,
    }));
  }
};

export const fetchDistributorsForCredits = async (): Promise<any[]> => {
  const requestData: HTTPParams = {
    method: "GET",
    url: "/api/retailer/credits/available_distributors",
  };

  const response = await HttpPromiseWithEntireResponse(requestData);

  if (response.ok) {
    const distributors = await response.json();
    return distributors
      .filter(d => d && d.name)
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  } else {
    throw new Error("Failed to fetch distributors for credits");
  }
};
